import { useState, useCallback } from 'react';
import axios from 'axios';
import { useSnackbar } from '../../../components/common/Snackbar';
import { ASSIGN_DIALOG_DEFAULTS } from '../../../constants/assignedQueriesPage';
import { WebConfig } from '../../../web-config';

/**
 * Hook to manage assignment dialog state and logic for AssignedQueries page
 * @param {Object} currentUser - Current user object
 * @param {Array} projectsCatalog - Projects list for project code lookup
 * @returns {Object} Assignment state and handlers
 */
export const useAssignedQueriesAssignment = (currentUser, projectsCatalog = []) => {
  const { showSnackbar } = useSnackbar();
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignAllUsers, setAssignAllUsers] = useState([]);
  const [assignRegion, setAssignRegion] = useState('');
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
  const [loadingAssignUsers, setLoadingAssignUsers] = useState(false);
  const [assignReason, setAssignReason] = useState(ASSIGN_DIALOG_DEFAULTS.reason);
  const [assignNote, setAssignNote] = useState(ASSIGN_DIALOG_DEFAULTS.note);

  const loadAssignableUsers = useCallback(
    async (query, regionValue) => {
      setLoadingAssignUsers(true);
      try {
        const storedUser =
          currentUser || JSON.parse(localStorage.getItem('user'));

        if (!storedUser) {
          showSnackbar('User information not found', 'error');
          setAssignUsers([]);
          setAssignRegion('');
          return;
        }

        // Resolve project code from catalog using query's projectName
        const catalog = Array.isArray(projectsCatalog) ? projectsCatalog : [];
        const projectName = query?.projectName || '';
        const matchedProject = catalog.find(
          (p) =>
            (p.description || '').toLowerCase() === projectName.toLowerCase() ||
            (p.name || '').toLowerCase() === projectName.toLowerCase()
        );
        const projectCode = matchedProject?.code || '';

        if (!projectCode) {
          showSnackbar('Project not found for this query', 'error');
          setAssignUsers([]);
          setAssignRegion(regionValue || '');
          return;
        }

        const response = await axios.get(
          `${WebConfig.REACT_APP_AAI_SSO_BASE_URL}/SEPL.PWIMS.AppServices/ProjectTarget/ProjectTarget.svc/GetProjectAssignee`,
          {
            params: {
              ProjectCode: projectCode,
              UserName: storedUser.name,
              orgId: 2,
            },
            withCredentials: false,
          }
        );

        const apiData = response?.data?.data || [];
        const projectUsers = apiData.flatMap((group) =>
          (group.Value || []).map((u) => ({ ...u, role: group.Key }))
        );

        // Exclude the current assignee from the list
        const currentAssigneeId = query?.assignee?._id?.toString() || '';
        const filtered = projectUsers
          .filter((u) => !currentAssigneeId || (u._id || '').toString() !== currentAssigneeId)
          .slice()
          .sort((a, b) =>
            (a.full_name || a.name || '').localeCompare(
              b.full_name || b.name || '',
              undefined,
              { sensitivity: 'base' }
            )
          );

        setAssignAllUsers(filtered);
        setAssignUsers(filtered);
        setAssignRegion(regionValue || '');
      } catch (error) {
        showSnackbar('Failed to load users', 'error');
        setAssignUsers([]);
        setAssignRegion(regionValue || '');
      } finally {
        setLoadingAssignUsers(false);
      }
    },
    [currentUser, showSnackbar, projectsCatalog]
  );

  const handleAssignRegionChange = (event) => {
    setAssignRegion(event.target.value);
  };

  const resetAssignDialog = () => {
    setSelectedAssigneeId('');
    setAssignReason(ASSIGN_DIALOG_DEFAULTS.reason);
    setAssignNote(ASSIGN_DIALOG_DEFAULTS.note);
    setAssignUsers([]);
    setAssignAllUsers([]);
    setAssignRegion('');
  };

  return {
    assignUsers,
    assignAllUsers,
    assignRegion,
    selectedAssigneeId,
    loadingAssignUsers,
    assignReason,
    assignNote,
    setAssignUsers,
    setAssignAllUsers,
    setAssignRegion,
    setSelectedAssigneeId,
    setLoadingAssignUsers,
    setAssignReason,
    setAssignNote,
    loadAssignableUsers,
    handleAssignRegionChange,
    resetAssignDialog,
  };
};
